import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import { actingFor } from '../api/client';
import { useLearner } from './LearnerContext';
import { CONSENT_TEXT, giveConsent, removeLearner, type Learner } from './learners';
import { useT } from '../i18n/i18n';

export default function ConsentGate({ children }: { children: ReactNode }) {
  const { learners, ready, refresh } = useLearner();
  const t = useT();
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<Learner | null>(null);

  const waiting = learners.filter((l) => !l.consentAt);
  if (!ready || actingFor() || waiting.length === 0) return <>{children}</>;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="consent-h" style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="card">
        <h2 id="consent-h">{t('Consent for your learners')}</h2>
        <p>
          {t("EasyLearning keeps a record of each learner's practice. How a learner practises can show that they have special educational needs, which the law treats as health information. So we need consent before we keep it.")}
        </p>
        <p className="field-hint">
          {t('You can withdraw consent at any time by removing the learner in Settings. That deletes everything recorded about them.')} <Link to="/privacy" target="_blank" rel="noopener">{t('Read the privacy notice')}</Link>.
        </p>

        {error && <div className="error" role="alert">{error}</div>}

        <ul className="list">
          {waiting.map((l) => (
            <li className="list-item consent-item" key={l.id}>
              <div className="list-item-text">
                <div className="name">{l.name}</div>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={!!ticked[l.id]}
                    onChange={(e) => setTicked((t) => ({ ...t, [l.id]: e.target.checked }))}
                  />
                  {t(CONSENT_TEXT)}
                </label>
              </div>
              <div className="toolbar">
                <button
                  className="btn"
                  disabled={busy || !ticked[l.id]}
                  onClick={() => void run(() => giveConsent(l.id))}
                >
                  {t('Give consent')}
                </button>
                {learners.length > 1 && (
                  <button
                    className="btn quiet-danger"
                    disabled={busy}
                    onClick={() => setPendingRemove(l)}
                  >
                    {t('Remove {name}', { name: l.name })}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        {learners.length === 1 && (
          <p className="field-hint">
            {t('If you do not want to give consent, you can delete your account from the menu at the top.')}
          </p>
        )}
        <ConfirmDialog
          open={pendingRemove !== null}
          title={t('Remove learner?')}
          message={pendingRemove ? t('"{name}" and everything recorded about their practice will be removed. This cannot be undone.', { name: pendingRemove.name }) : ''}
          confirmLabel={busy ? t('Removing…') : t('Remove')}
          cancelLabel={t('Keep')}
          destructive
          onConfirm={() => { const l = pendingRemove; setPendingRemove(null); if (l) void run(() => removeLearner(l.id)); }}
          onCancel={() => { if (!busy) setPendingRemove(null); }}
        />
      </div>
    </section>
  );
}
